import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Volume2, Home, RotateCcw, Check } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { api } from "../api";

/* -------------------------------------------------------------------------
 * Small helper: play a WAV buffer given base64. Returns a cleanup fn.
 * Autoplay may be blocked until user interacts — we swallow that gracefully.
 * -----------------------------------------------------------------------*/
function useQuestionAudio(question, lang) {
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!question) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Bundle question + 3 options as ONE narration.
    const speak =
      `${question.question}. ` +
      question.options.map((o) => `${o.letter}. ${o.text}`).join(". ");
    api
      .post("/daily-reflection/audio", { text: speak, language: lang })
      .then((res) => {
        if (cancelled) return;
        const b64 = res.data?.audio_b64;
        if (!b64) {
          setError(res.data?.audio_error || "TTS unavailable");
          return;
        }
        const src = `data:${res.data.audio_mime || "audio/wav"};base64,${b64}`;
        if (audioRef.current) {
          audioRef.current.src = src;
          audioRef.current.play().catch(() => { /* autoplay may be blocked */ });
        }
      })
      .catch(() => {
        if (!cancelled) setError("audio_failed");
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      }
    };
  }, [question?.row_index, lang]);

  return { audioRef, loading, error };
}

/* -------------------------------------------------------------------------
 * Progress dots — one dot per question.
 * -----------------------------------------------------------------------*/
function ProgressDots({ total, current }) {
  return (
    <div className="flex items-center justify-center gap-2" data-testid="dr-progress-dots">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={
            "h-2 rounded-full transition-all duration-500 " +
            (i < current
              ? "w-8 bg-emerald-500"
              : i === current
              ? "w-10 bg-violet-500"
              : "w-6 bg-slate-300")
          }
        />
      ))}
    </div>
  );
}

/* =========================================================================
 * Main page
 * =======================================================================*/
export default function DailyReflection() {
  const { user, lang } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({}); // { row_index: "A"|"B"|"C" }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/daily-reflection/session`, { params: { language: lang } })
      .then((res) => { if (!cancelled) setSession(res.data); })
      .catch((e) => { if (!cancelled) setLoadErr(e?.response?.data?.detail || e.message); })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [lang]);

  const questions = session?.questions || [];
  const total = questions.length;
  const currentQ = questions[step];
  const chosenLetter = currentQ ? answers[currentQ.row_index] : null;

  const { audioRef, error: audioErr } = useQuestionAudio(currentQ, lang);

  const pick = (letter) => {
    if (!currentQ) return;
    setAnswers((prev) => ({ ...prev, [currentQ.row_index]: letter }));
  };

  const goNext = async () => {
    if (step < total - 1) {
      setStep(step + 1);
      return;
    }
    // Final step — submit.
    const row_indexes = questions.map((q) => q.row_index);
    const chosen_letters = row_indexes.map((r) => answers[r]);
    try {
      const res = await api.post("/daily-reflection/submit", {
        row_indexes,
        chosen_letters,
        language: lang,
      });
      sessionStorage.setItem("dr_result", JSON.stringify({
        ...res.data,
        session_questions: questions,
      }));
      navigate("/daily-reflection/result");
    } catch (e) {
      toast.error("Submit failed", { description: e?.message || "Try again" });
    }
  };

  const replayAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.currentTime = 0; audioRef.current.play(); } catch (_) { /* ignore */ }
    }
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div className="page-shell" data-testid="dr-loading">
        <TopBar lang={lang} back onBack={() => navigate("/")} />
        <div className="mt-24 flex flex-col items-center gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "linear" }}
            className="w-16 h-16 rounded-full border-4 border-violet-300 border-t-violet-600"
          />
          <p className="font-heading text-lg font-bold text-white drop-shadow-[0_2px_8px_rgba(15,23,42,0.5)]">{t(lang, "dr_loading")}</p>
        </div>
      </div>
    );
  }

  if (loadErr || !session) {
    return (
      <div className="page-shell" data-testid="dr-error">
        <TopBar lang={lang} back onBack={() => navigate("/")} />
        <div className="mt-20 bg-white p-6 rounded-3xl border-4 border-white text-center">
          <p className="font-heading text-lg font-bold text-slate-800">Something went wrong</p>
          <p className="text-sm text-slate-600 mt-2">{loadErr || "Please try again."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell" data-testid="daily-reflection-page">
      <TopBar lang={lang} back onBack={() => navigate("/")} />

      {/* Header — title + progress */}
      <div className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={14} strokeWidth={2.5} className="text-violet-500" />
          {t(lang, "daily_reflection")}
        </div>
        <p className="mt-3 text-sm text-cyan-50/90 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]" data-testid="dr-progress-text">
          {t(lang, "dr_question_of").replace("{n}", step + 1).replace("{total}", total)}
        </p>
        <div className="mt-3">
          <ProgressDots total={total} current={step} />
        </div>
      </div>

      {/* Category chip */}
      <div className="mt-6 flex items-center justify-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-heading font-bold uppercase tracking-wider"
          data-testid="dr-category"
        >
          <Sparkles size={12} strokeWidth={2.75} />
          {currentQ?.category}
        </span>
      </div>

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQ?.row_index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="mt-4 bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
          data-testid="dr-question-card"
        >
          <p
            className="font-heading text-lg font-bold text-slate-800 leading-snug"
            data-testid="dr-question-text"
          >
            {currentQ?.question}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <audio ref={audioRef} style={{ display: "none" }} />
            <button
              type="button"
              onClick={replayAudio}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-violet-600 bg-violet-100 hover:bg-violet-200 rounded-full px-3 py-1.5 btn-squish"
              data-testid="dr-listen-btn"
            >
              <Volume2 size={14} strokeWidth={2.75} />
              {t(lang, "dr_listen")}
            </button>
            {audioErr && (
              <span className="text-[10px] text-slate-400 font-semibold">
                (audio: {audioErr})
              </span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Options */}
      <div className="mt-4 flex flex-col gap-3" data-testid="dr-options">
        {currentQ?.options.map((opt) => {
          const selected = chosenLetter === opt.letter;
          const palette = optionPalette(opt.letter, selected);
          return (
            <motion.button
              key={opt.letter}
              onClick={() => pick(opt.letter)}
              whileTap={{ scale: 0.97 }}
              data-testid={`dr-option-${opt.letter}-btn`}
              className={
                "text-left flex items-start gap-3 rounded-2xl p-4 border-4 transition-all " +
                palette.card
              }
            >
              <span
                className={
                  "shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-base " +
                  palette.badge
                }
              >
                {selected ? <Check size={18} strokeWidth={3} /> : opt.letter}
              </span>
              <span className={"font-semibold leading-snug " + palette.text}>{opt.text}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Next / Finish */}
      <div className="mt-5">
        <button
          onClick={goNext}
          disabled={!chosenLetter}
          data-testid="dr-next-btn"
          className={
            "w-full py-4 rounded-full font-heading font-bold uppercase tracking-tight text-sm btn-squish inline-flex items-center justify-center gap-2 " +
            (chosenLetter
              ? "bg-violet-500 hover:bg-violet-600 text-white shadow-[0_6px_0_rgba(0,0,0,0.15)]"
              : "bg-slate-200 text-slate-400 cursor-not-allowed")
          }
        >
          {step < total - 1 ? t(lang, "dr_next") : t(lang, "dr_finish")}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
function TopBar({ lang, back, onBack }) {
  return (
    <div className="flex items-center justify-between">
      {back ? (
        <button
          onClick={onBack}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          data-testid="dr-back-btn"
          aria-label="back"
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-600" />
        </button>
      ) : <span />}
      <span className="font-heading font-bold text-sm text-white drop-shadow-[0_2px_8px_rgba(15,23,42,0.5)] truncate">
        {t(lang, "daily_reflection")}
      </span>
      <span className="w-9" />
    </div>
  );
}

function optionPalette(letter, selected) {
  const map = {
    A: {
      card: selected ? "bg-amber-300 border-amber-400" : "bg-amber-100 border-white hover:bg-amber-200",
      badge: selected ? "bg-white text-amber-700" : "bg-amber-500 text-white",
      text: selected ? "text-amber-900" : "text-slate-800",
    },
    B: {
      card: selected ? "bg-sky-300 border-sky-400" : "bg-sky-100 border-white hover:bg-sky-200",
      badge: selected ? "bg-white text-sky-700" : "bg-sky-500 text-white",
      text: selected ? "text-sky-900" : "text-slate-800",
    },
    C: {
      card: selected ? "bg-emerald-300 border-emerald-400" : "bg-emerald-100 border-white hover:bg-emerald-200",
      badge: selected ? "bg-white text-emerald-700" : "bg-emerald-500 text-white",
      text: selected ? "text-emerald-900" : "text-slate-800",
    },
  };
  return map[letter] || map.A;
}
